// src/pages/Inquiry/PrintForm4.jsx
import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { fetchInquiry } from "../../../store/slices/inquirySlice";
import logo from "../../../assets/images/logo-samplee.png";

export default function InquiryPrint() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { currentInquiry, isLoading } = useSelector((state) => state.inquiries);

  useEffect(() => {
    if (id) dispatch(fetchInquiry(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (currentInquiry) {
      const timer = setTimeout(() => window.print(), 500);
      window.onafterprint = () => window.close();
      return () => clearTimeout(timer);
    }
  }, [currentInquiry]);

  if (isLoading) return <div className="p-5 text-center">Loading...</div>;
  if (!currentInquiry) return <div className="p-5 text-center text-danger">No data found</div>;

  const m = currentInquiry.master || {};
  const programId = m.program_master_id;
  const ci = currentInquiry.clientInformation || {};
  const med = currentInquiry.clientMedications || {};
  const hist = currentInquiry.clientHistory || {};
  const risk = currentInquiry.riskForm || {};
  const agg = currentInquiry.aggressionHistory || {};
  const beh = currentInquiry.clientBehavior || {};
  const treatment = currentInquiry.treatmentPlan || {};

  const hear = currentInquiry.hearAboutUs || [];
  const medsList = currentInquiry.medicationsList || [];
  const hospList = currentInquiry.prevHospitalizations || [];
  const prevServices = med.prev_services ? JSON.parse(med.prev_services) : [];

  const hasFinanceInfo = m.amount || m.payment_status || m.reference_no || m.payment_date;

  const formatDate = (dateStr) => (dateStr ? dateStr.split("T")[0] : " ");
  const formatTime = (date) => date ? new Date(date).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "—";

  const calculateAge = (dob) => {
    if (!dob) return " ";
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) age--;
    return age;
  };

  const isHearChecked = (value) => hear.some(h => h.value.includes(value));
  const getOtherHear = () => hear.find(h => h.value === "Other")?.name || " ";

  const checkbox = (condition) => (
    <span className={`checkbox ${condition ? "checked" : ""}`}></span>
  );

  const yesNo = (val) => (
    <>
      Yes {checkbox(val === "Yes")} &nbsp;&nbsp; No {checkbox(val === "No")}
    </>
  );

  const isDayRehab = programId === 1;
  const isHomeVisitation = programId === 2;
  const isResidential = programId === 3;

  return (
    <>
      <style jsx global>{`
        * { box-sizing: border-box !important; }
        body { font-family: Arial, sans-serif !important; font-size: 11pt !important; line-height: 1.5 !important; color: #000 !important; background: #fff !important; margin: 0 !important; padding: 0 !important; }
        @page {
          size: auto;
          margin: 10mm !important;
        }
        .print-container { padding: 10px !important; position: relative !important; }
        .master-print-table {
          width: 100% !important;
          border-collapse: collapse !important;
          border: none !important;
        }
        .master-print-table > tbody > tr > td,
        .master-print-table > tfoot > tr > td {
          border: none !important;
          padding: 0 !important;
        }
        .print-footer {
          text-align: center !important;
          font-size: 0.85rem !important;
          color: #555 !important;
          border-top: 1px solid #ccc !important;
          padding-top: 8px !important;
          padding-bottom: 8px !important;
          margin-top: 15px !important;
        }
        .logo { position: absolute !important; top: 10px !important; left: 10px !important; z-index: 1000 !important; }
        .logo img { height: 70px !important; width: auto !important; }
        .header h3 { font-size: 1.5em !important; font-weight: bold !important; text-align: center !important; border-bottom: 1.5px solid #383838 !important; padding-bottom: 15px !important; margin-bottom: 10px !important; }
        .header h6 { font-size: 1.2em !important; text-align: center !important; margin-bottom: 5px !important; font-weight: 600 !important; }
        table { width: 100% !important; border-collapse: collapse !important; margin: 10px 0 !important; font-size: 0.95em !important; }
        td { border: 1px solid #000 !important; padding: 7px 10px !important; vertical-align: top !important; }
        .label { background: #f0f0f0 !important; width: 15% !important; }
        .checkbox { display: inline-block !important; width: 13px !important; height: 13px !important; border: 1.5px solid #000 !important; margin: 0 6px 0 8px !important; vertical-align: middle !important; }
        .checked::before { content: "✓" !important; font-weight: bold !important; font-size: 11px !important; position: relative !important; bottom: 5px !important; left: 1px !important; }
        .section { font-size: 1.2em !important; font-weight: 600 !important; margin: 25px 0 8px !important; }
        .center { text-align: center !important; }
        .area { border: 1px solid #000 !important; min-height: 70px !important; padding: 8px !important; }
        @media print { body { padding: 10px !important; } .print-container { padding: 15px 15px 60px 15px !important; } }
      `}</style>

      <div className="logo">
        <img src={logo} alt="Logo" />
      </div>

      <div className="print-container">
        <table className="master-print-table">
          <tbody>
            <tr>
              <td>
                <div className="header">
                  <h3>THE RECOVERY HOUSE</h3>
                  <h6>INQUIRY FORM — {m.program?.name || "General"}</h6>
                </div>

        {/* COMMON SECTIONS — ALWAYS SHOWN */}
        <table style={{ marginTop: "20px" }}>
          <tbody>
            <tr>
              <td width="50%">Date: <u>{formatDate(m.created_at)}</u></td>
              <td width="50%">Time: <u>{formatTime(m.created_at)}</u></td>
            </tr>
          </tbody>
        </table>

        <div className="section">Contact Method</div>
        <table>
          <tbody>
            <tr>
              <td>Phone {checkbox(m.contact_method === "Phone")}</td>
              <td>Walk-In {checkbox(m.contact_method === "Walk-In")}</td>
              <td>Social Media {checkbox(m.contact_method === "Social Media")}</td>
              <td>Appointment {checkbox(m.contact_method === "Appointment")}</td>
            </tr>
          </tbody>
        </table>

        <div className="section">Inquirer Information</div>
        <table>
          <tbody>
            <tr>
              <td className="label">Name</td>
              <td><strong>{m.inquirer_name || " "}</strong></td>
              <td width="25%">Relation with Client</td>
              <td><strong>{m.relation_with_client || " "}</strong></td>
            </tr>
            <tr>
              <td className="label">Phone 1</td>
              <td><strong>{m.phone1 || " "}</strong></td>
              <td className="label">Phone 2</td>
              <td><strong>{m.phone2 || " "}</strong></td>
            </tr>
            <tr>
              <td width="25%">Email Address</td>
              <td colSpan={3}><strong>{m.email || " "}</strong></td>
            </tr>
            <tr>
              <td className="label">Address</td>
              <td colSpan={3}><strong>{m.address || " "}</strong></td>
            </tr>
          </tbody>
        </table>

        <div className="section">How did you hear about us?</div>
        <table>
          <tbody>
            <tr>
              <td width="50%">
                {checkbox(isHearChecked("Psychiatrist"))} Referred by Psychiatrist<br />
                {checkbox(isHearChecked("Psychologist"))} Referred by Psychologist<br />
                {checkbox(isHearChecked("Medical Practitioner"))} Other Medical Practitioner<br />
                {checkbox(isHearChecked("Brochure/Flyer"))} TRH Brochure/Flyer<br />
                {checkbox(isHearChecked("Website"))} Website<br />
                {checkbox(isHearChecked("Advertisement"))} Advertisement
              </td>
              <td width="50%">
                {checkbox(isHearChecked("Social media of TRH"))} Social media of TRH<br />
                {checkbox(isHearChecked("Seminar/Event"))} Seminar/Event<br />
                {checkbox(isHearChecked("Saw the Signboard"))} Saw the Signboard<br />
                {checkbox(isHearChecked("Word of Mouth"))} Word of Mouth<br />
                {checkbox(isHearChecked("TV"))} TV<br />
                {checkbox(isHearChecked("Other"))} Other: <u>{getOtherHear()}</u>
              </td>
            </tr>
          </tbody>
        </table>

        <div className="section">Client Information</div>
        <table>
          <tbody>
            <tr>
              <td className="label">Name</td>
              <td colSpan={2}><strong>{m.patient?.first_name} {m.patient?.last_name}</strong></td>
              <td className="label">Age</td>
              <td><strong>{calculateAge(ci.client_dob || m.patient?.dob)}</strong></td>
              <td className="label">Gender</td>
              <td><strong>{ci.client_gender || m.patient?.gender}</strong></td>
            </tr>
            <tr>
              <td className="label">CNIC</td>
              <td colSpan={3}><strong>{ci.client_cnic || m.patient?.cnic}</strong></td>
              <td className="label">Phone</td>
              <td colSpan={2}><strong>{ci.client_phone || m.patient?.phone}</strong></td>
            </tr>
            <tr>
              <td colSpan={2} className="label">Email</td>
              <td colSpan={5}><strong>{ci.client_email || m.patient?.email}</strong></td>
            </tr>
            <tr>
              <td className="label">Address</td>
              <td colSpan={6}><strong>{ci.client_address || m.patient?.address}</strong></td>
            </tr>
            <tr>
              <td className="label">Education</td>
              <td><strong>{ci.client_education || " "}</strong></td>
              <td colSpan={2} className="label">No. of Siblings</td>
              <td><strong>{ci.client_siblings || " "}</strong></td>
              <td className="label">Birth Order</td>
              <td><strong>{ci.client_birth_order || " "}</strong></td>
            </tr>
            <tr>
              <td colSpan={3} className="label">Family History of Mental Illness</td>
              <td colSpan={2}>{yesNo(ci.family_mental_illness)}</td>
              <td colSpan={2}><strong>{ci.family_mental_illness_details || "N/A" && <u>{ci.family_mental_illness_details}</u>}</strong></td>
            </tr>
            <tr>
              <td className="label">Diagnosis</td>
              <td colSpan={3}><strong>{ci.diagnosis || " "}</strong></td>
              <td className="label">Since</td>
              <td colSpan={2}><strong>{formatDate(ci.diagnosis_since)}</strong></td>
            </tr>
            <tr>
              <td className="label" colSpan={2}>Presenting complaints</td>
              <td colSpan={5}><strong>{ci.presenting_complaints || " "}</strong></td>
            </tr>
            <tr>
              <td className="label" colSpan={2}>Currently under the care of</td>
              <td colSpan={5}><strong>{ci.current_care || " "}</strong></td>
            </tr>
            <tr>
              <td colSpan={3}>Previous TRH Services?</td>
              <td colSpan={2}>{yesNo(ci.trh_services)}</td>
              <td colSpan={2}> {ci.trh_services_details || "N/A" && <u>{ci.trh_services_details}</u>}</td>
            </tr>
            <tr>
              <td colSpan={3}>Substance Use History?</td>
              <td colSpan={2}>{yesNo(ci.substance_use)}</td>
              <td colSpan={2}> {ci.substance_use_details || "N/A" && <u>{ci.substance_use_details}</u>}</td>
            </tr>
          </tbody>
        </table>

        <div className="section">List of Current Medications</div>
        <table>
          <tbody>
            {medsList.length > 0 ? medsList.map((item, i) => (
              <tr key={i}>
                <td colSpan={2}>{i + 1}. <strong>{item.name || " "}</strong></td>
                <td colSpan={2}>Dosage: <strong>{item.dosage || " "}</strong></td>
              </tr>
            )) : <tr><td colSpan={4}>No medications listed</td></tr>}
            <tr>
              <td>Prescribed by</td>
              <td><strong>Dr. {med.prescribed_by || " "}</strong></td>
              <td>Date</td>
              <td><strong>{formatDate(med.prescription_date)}</strong></td>
            </tr>
          </tbody>
        </table>

        <div className="section">Mental Health Services Previously Availed</div>
        <table>
          <tbody>
            <tr>
              <td width="50%">
                {checkbox(isHearChecked("Psychiatrist"))} OPD with Psychiatrist<br />
                {checkbox(isHearChecked("Psychologist"))} OPD with Therapist<br />
                {checkbox(isHearChecked("Medical Practitioner"))} In-patient Psychiatric Services<br />
                {checkbox(isHearChecked("Brochure/Flyer"))} Psychiatric Rehab Services<br />
                {checkbox(isHearChecked("Website"))} Substance-Related Treatment<br />
              </td>
              <td width="50%">
                {checkbox(isHearChecked("Advertisement"))} Occupational Therapy
                {checkbox(isHearChecked("Social media of TRH"))} Electroconvulsive Therapy (ECT)<br />
                {checkbox(isHearChecked("Seminar/Event"))} Spiritual Treatment<br />
                {checkbox(isHearChecked("Other"))} Other: <u>{getOtherHear()}</u>
              </td>
            </tr>
          </tbody>
        </table>

        <div className="section">History of Previous Psychiatric Hospitalizations</div>
        <table>
          <tbody>
            {hospList.length > 0 ? (
              hospList.map((item, i) => (
                <tr key={item.id || i}>
                  <td colSpan={2}>
                    {i + 1}. Hospital: <strong>{item.hospital || " "}</strong>
                  </td>
                  <td colSpan={2}>
                    Duration: <strong>{item.duration || " "}</strong>
                  </td>
                  <td colSpan={2}>
                    Consultant: <strong>{item.consultant || " "}</strong>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6}>No hospitalizations listed</td>
              </tr>
            )}
          </tbody>
        </table>

        {/* PROGRAM-SPECIFIC SECTIONS */}

        {/* HOME VISITATION PROGRAM (ID: 2) */}
        {isHomeVisitation && (
          <>
            <div className="section center">HOME VISITATION ASSESSMENT</div>

            <div className="section">Aggression History</div>
            <table>
              <tbody>
                <tr>
                  <td width="40%">Previous History of Aggression?</td>
                  <td>{yesNo(agg.prev_aggression)}</td>
                </tr>
                <tr>
                  <td>Last Episode</td>
                  <td><u>{agg.last_episode || "—"}</u></td>
                </tr>
                <tr>
                  <td>Incident Provoked?</td>
                  <td>{yesNo(agg.incident_provoked)}</td>
                </tr>
                <tr>
                  <td>Regret After Incident?</td>
                  <td>{yesNo(agg.regret_incident)}</td>
                </tr>
                <tr>
                  <td>Show Denial?</td>
                  <td>{yesNo(agg.show_denial)}</td>
                </tr>
                <tr>
                  <td>Episode Description</td>
                  <td><div className="area">{agg.episode_description || "—"}</div></td>
                </tr>
                <tr>
                  <td>How Does Client Cope With Stress?</td>
                  <td><div className="area">{agg.cope_stress || "—"}</div></td>
                </tr>
              </tbody>
            </table>

            <div className="section">Client Behavior Assessment</div>
            <table>
              <tbody>
                <tr>
                  <td width="40%">General Behavior</td>
                  <td>
                    {beh.client_general_behavior || "—"}
                  </td>
                </tr>
                <tr>
                  <td>Impulsive Behavior?</td>
                  <td>{yesNo(beh.impulsive_behavior)}</td>
                </tr>
                {beh.impulsive_behavior === "Yes" && (
                  <>
                    <tr><td>Example 1</td><td><u>{beh.impulsive_example_1 || "—"}</u></td></tr>
                    <tr><td>Example 2</td><td><u>{beh.impulsive_example_2 || "—"}</u></td></tr>
                    <tr><td>Example 3</td><td><u>{beh.impulsive_example_3 || "—"}</u></td></tr>
                  </>
                )}
                <tr>
                  <td>Current Symptoms</td>
                  <td><div className="area">{beh.symptoms || "—"}</div></td>
                </tr>
                <tr>
                  <td>Expectations from Rehab</td>
                  <td><div className="area">{beh.rehab_expectations || "—"}</div></td>
                </tr>
                <tr>
                  <td>Fear of Aggression from Client?</td>
                  <td>{yesNo(beh.fear_aggression)}</td>
                </tr>
                <tr>
                  <td>Psychiatrist Note</td>
                  <td><div className="area">{beh.psychiatrist_note || "—"}</div></td>
                </tr>
              </tbody>
            </table>
            <div className="section">Treatment Plan</div>
            <table>
              <tbody>
                <tr>
                  <td width="40%">Recommended Treatment Option</td>
                  <td>
                    {treatment?.treatment_option
                      ? treatment.treatment_option.replaceAll("_", " ").toUpperCase()
                      : "—"}
                  </td>
                </tr>
                <tr>
                  <td>Reason</td>
                  <td>
                    <div className="area">
                      {treatment?.reason || "—"}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

          </>
        )}

        {/* RESIDENTIAL REHAB (ID: 3) — YOUR ORIGINAL SECTIONS */}
        {(isResidential || isDayRehab) && (
          <>
            {/* Your existing Client History + Risk Form stays exactly as you had */}
            <div className="section">Physical Health Information</div>
            <p>(Family to Complete)</p>
            <table>
              <tbody>
                <tr>
                  <td colSpan={2}>Known medical conditions</td>
                  <td>{yesNo(hist.medical_conditions)} <strong>{hist.medical_conditions_details || " "}</strong></td>
                </tr>
              </tbody>
            </table>

            <div className="section">Has the individual experienced any of the following</div>
            <p>(All That Apply)</p>
            <table>
              <tbody>
                <tr>
                  <td>{checkbox(hist.experienced_issues?.includes("High Blood Pressure"))} High BP</td>
                  <td>{checkbox(hist.experienced_issues?.includes("Diabetes"))} Diabetes</td>
                  <td>{checkbox(hist.experienced_issues?.includes("Heart Disease"))} Heart Disease</td>
                </tr>
                <tr>
                  <td>{checkbox(hist.experienced_issues?.includes("Respiratory"))} Respiratory</td>
                  <td>{checkbox(hist.experienced_issues?.includes("Weight Issues"))} Weight Issues</td>
                  <td>{checkbox(hist.experienced_issues?.includes("Other"))} Other: <u>{hist.other_health_issue || " "}</u></td>
                </tr>
              </tbody>
            </table>

            <div className="section">Brief History of Illness</div>
            <div className="area">{hist.illness_history || " "}</div>

            <div className="section">Expectations from Rehabilitation Services at TRH</div>
            <div className="area">{hist.expectations || " "}</div>

            <div className="section center">Risk Assessment Form</div>
            <div className="section">Section A: Suicide Risk</div>
            <table>
              <tbody>
                <tr>
                  <td colSpan={2}>Suicidal Thoughts</td>
                  <td>{yesNo(risk.suicidal_thoughts)}</td>
                  <td colSpan={2}>Current or past suicide plans</td>
                  <td>{yesNo(risk.current_or_past_suicide_plans)}</td>
                </tr>
                <tr>
                  <td colSpan={3}>Frequency of suicidal thoughts</td>
                  <td>{checkbox(risk.frequency_suicidal_thoughts === "Rare")} Rare</td>
                  <td>{checkbox(risk.frequency_suicidal_thoughts === "Occasional")} Occasional</td>
                  <td>{checkbox(risk.frequency_suicidal_thoughts === "Frequent")} Frequent</td>
                </tr>
                <tr>
                  <td colSpan={2}>Previous suicide attempts</td>
                  <td>{yesNo(risk.previous_suicide_attempts)}</td>
                  <td colSpan={3}><u>{risk.previous_suicide_attempts_details || " "}</u></td>
                </tr>
              </tbody>
            </table>

            <div className="section">Section B: Escape Risk</div>
            <table>
              <tbody>
                <tr>
                  <td colSpan={2}>Desire to escape</td>
                  <td>{yesNo(risk.desire_to_escape)}</td>
                  <td colSpan={2}>Past escape attempts</td>
                  <td>{yesNo(risk.past_escape_attempts)}</td>
                </tr>
                <tr>
                  <td>Details</td>
                  <td colSpan={5}><u>{risk.past_escape_attempts_details || " "}</u></td>
                </tr>
              </tbody>
            </table>

            <div className="section">Section C: Risk of Aggression or Violence</div>
            <table>
              <tbody>
                <tr>
                  <td>History of aggression</td>
                  <td>{yesNo(risk.history_of_aggression)}</td>
                  <td colSpan={4}><u>{risk.history_of_aggression_details || " "}</u></td>
                </tr>
                <tr>
                  <td>Current risk of harm to others</td>
                  <td>{yesNo(risk.current_risk_of_harm_to_others)}</td>
                  <td colSpan={4}><u>{risk.specific_individuals || " "}</u></td>
                </tr>
              </tbody>
            </table>

            <div className="section">Section D: Self-Harm Risk</div>
            <table>
              <tbody>
                <tr>
                  <td>History of self-harm</td>
                  <td>{yesNo(risk.history_of_self_harm)}</td>
                  <td colSpan={4}><u>{risk.history_of_self_harm_details || " "}</u></td>
                </tr>
                <tr>
                  <td>Current self-harm urges</td>
                  <td>{yesNo(risk.current_self_harm_urges)}</td>
                  <td colSpan={4}><u>{risk.specific_self_harm_actions || " "}</u></td>
                </tr>
              </tbody>
            </table>

            <div className="section">Recommended for:</div>
            <table>
              <tbody>
                <tr>
                  <td>{checkbox(risk.recommended_for?.includes("Residential Rehab Program"))} Residential Rehab Program</td>
                  <td>{checkbox(risk.recommended_for?.includes("Day Rehab Program"))} Day Rehab Program</td>
                </tr>
                <tr>
                  <td>{checkbox(risk.recommended_for?.includes("OPD Services outside TRH"))} OPD Services outside TRH:</td>
                  <td><u>{risk.opd_details || "N/A"}</u></td>
                </tr>
              </tbody>
            </table>

            <div className="section">Follow-up Notes:</div>
            <div className="area">{risk.follow_up_notes || " "}</div>
          </>
        )}

        {hasFinanceInfo && (
          <>
            <div className="section">Finance Information</div>
            <table>
              <tbody>
                <tr>
                  <td className="label">Amount</td>
                  <td><strong>{m.amount || " "}</strong></td>
                  <td className="label">Payment Status</td>
                  <td><strong>{m.payment_status || " "}</strong></td>
                </tr>
                <tr>
                  <td className="label">Reference No</td>
                  <td><strong>{m.reference_no || " "}</strong></td>
                  <td className="label">Payment Date</td>
                  <td><strong>{formatDate(m.payment_date)}</strong></td>
                </tr>
              </tbody>
            </table>
          </>
        )}
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td>
                <div className="print-footer">
                  This is a system generated print no signature required, <strong>NOT VALID FOR COURT</strong>
                </div>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  );
}